import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2, FileSpreadsheet, FileText, Loader2, Search, X, Image as ImageIcon, Save, Printer, MessageCircle, MapPin
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { exportExcel, exportPdfSections, fmtDate, fmtPhone } from '@/lib/exporters';
import { PLAQUE_FORMATS, generatePlaque } from '@/lib/plaques';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const token = () => localStorage.getItem('token');
const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

const STATUS_LABEL = {
  active: 'Ativo', trial: 'Trial', overdue: 'Inadimplente', suspended: 'Inadimplente',
  canceled: 'Cancelado', cancelled: 'Cancelado', none: 'Sem cobrança',
};
const STATUS_STYLE = {
  active: 'bg-emerald-100 text-emerald-700', trial: 'bg-blue-100 text-blue-700',
  overdue: 'bg-red-100 text-red-700', suspended: 'bg-red-100 text-red-700',
  canceled: 'bg-slate-200 text-slate-600', cancelled: 'bg-slate-200 text-slate-600',
  none: 'bg-slate-100 text-slate-500',
};

const Field = ({ label, value, onChange, disabled }) => (
  <div>
    <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">{label}</label>
    <input
      value={value || ''}
      onChange={e => onChange?.(e.target.value)}
      disabled={disabled}
      className={`w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm ${disabled ? 'bg-slate-50 text-slate-500' : ''}`}
      data-testid={`field-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
    />
  </div>
);

const CompanyProfile = ({ companyId, onClose, onSaved }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [plaqueLoading, setPlaqueLoading] = useState(null);
  const [waOpen, setWaOpen] = useState(false);
  const [waPhone, setWaPhone] = useState('');
  const [waNote, setWaNote] = useState('Oi! Estou mandando a sua plaquinha do Falo Com O Dono. É só imprimir e colocar no balcão. 😊');
  const [waSending, setWaSending] = useState(false);

  const sendPlaqueWhatsapp = async () => {
    if (!window.confirm(`Enviar a plaquinha para ${waPhone}?`)) return;
    setWaSending(true);
    try {
      const res = await axios.post(`${API}/admin/registry/company/${companyId}/send-plaque`,
        { phone: waPhone, message: waNote }, auth());
      toast.success(res.data.sent ? 'Plaquinha enviada no WhatsApp!' : `Não enviado: ${res.data.reason}`);
      setWaOpen(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao enviar'); }
    finally { setWaSending(false); }
  };

  const downloadPlaque = async (format) => {
    setPlaqueLoading(format);
    try {
      const logoRes = await axios.get(`${API}/admin/registry/company/${companyId}/logo-base64`, auth());
      const logo = logoRes.data?.base64
        ? {
          dataUrl: logoRes.data.base64, width: logoRes.data.width, height: logoRes.data.height,
          format: logoRes.data.base64.startsWith('data:image/png') ? 'PNG' : 'JPEG',
        }
        : null;
      await generatePlaque(format, {
        name: data.basics.name, slug: data.config.slug,
        primary_color: data.config.primary_color, secondary_color: data.config.secondary_color,
        owner_title: data.config.owner_title, logo,
      });
      toast.success('Plaquinha gerada!');
    } catch (e) { toast.error('Erro ao gerar a plaquinha: ' + (e.message || '')); }
    finally { setPlaqueLoading(null); }
  };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/registry/company/${companyId}`, auth());
      setData(res.data);
      setForm({ ...res.data.basics, ...res.data.config });
    } catch { toast.error('Erro ao abrir a ficha'); }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/company/${companyId}/info`, {
        company_name: form.name, document: form.document, owner_name: form.owner_name,
        owner_email: form.owner_email, owner_whatsapp: form.owner_whatsapp, slug: form.slug,
      }, auth());
      await axios.put(`${API}/admin/registry/company/${companyId}`, {
        address: form.address, city: form.city, state: form.state, phone: form.phone,
        contact_email: form.contact_email, logo_url: form.logo_url,
        google_maps_url: form.google_maps_url, primary_color: form.primary_color,
      }, auth());
      toast.success('Ficha salva!');
      await load();
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const exportFicha = () => {
    const b = data.basics, c = data.config, bi = data.billing, n = data.numbers;
    exportPdfSections(`Ficha · ${b.name}`, [
      { title: 'Dados básicos', rows: [
        ['Empresa', b.name], ['CNPJ/CPF', b.document || '—'], ['Endereço', b.address || '—'],
        ['Cidade/UF', b.city ? `${b.city}/${b.state}` : '—'], ['Telefone', fmtPhone(b.phone)],
        ['E-mail', b.contact_email || '—'], ['Dono', b.owner_name || '—'],
        ['E-mail do dono', b.owner_email || '—'], ['WhatsApp do dono', fmtPhone(b.owner_whatsapp)],
      ] },
      { title: 'Configurações', rows: [
        ['Link (slug)', `/${c.slug}`], ['Logomarca', c.logo_url ? 'cadastrada' : 'faltando'],
        ['Link do Google', c.google_maps_url || 'faltando'], ['Cor da placa', c.primary_color],
        ['Banco de clientes', c.collect_contacts ? 'ligado' : 'desligado'],
      ] },
      { title: 'Plano e financeiro', rows: [
        ['Plano', bi.plan_name], ['Status', STATUS_LABEL[bi.status] || bi.status],
        ['Ativação', fmtDate(bi.activated_at)],
        ['Último pagamento', bi.last_payment ? `R$ ${bi.last_payment.value} em ${fmtDate(bi.last_payment.date)}` : '—'],
        ['Próximo vencimento', fmtDate(bi.next_due_date)],
      ] },
      { title: 'Números', rows: [
        ['Total de avaliações', String(n.total_feedbacks)], ['Positivas', String(n.positives)],
        ['Negativas', String(n.negatives)], ['Nota média', String(n.rating_average)],
        ['Contatos coletados', String(n.total_contacts)],
      ] },
    ], `ficha-${c.slug}`);
  };

  const exportFeedbacks = async () => {
    const res = await axios.get(`${API}/admin/registry/company/${companyId}/feedbacks`, auth());
    if (!res.data.items.length) return toast.error('Esta empresa ainda não tem avaliações');
    exportExcel(res.data.items.map(f => ({
      Data: fmtDate(f.created_at), Cliente: f.customer_name, WhatsApp: fmtPhone(f.customer_whatsapp),
      'E-mail': f.customer_email, Tipo: f.feedback_type, Produtos: f.rating_food,
      Atendimento: f.rating_service, Ambiente: f.rating_ambiance, Média: f.rating_average,
      Mensagem: f.message, Urgente: f.is_urgent ? 'SIM' : 'NÃO',
    })), `avaliacoes-${data.config.slug}`, 'Avaliações');
  };

  const exportContacts = async () => {
    const res = await axios.get(`${API}/admin/registry/company/${companyId}/contacts`, auth());
    if (!res.data.items.length) return toast.error('Esta empresa ainda não tem contatos coletados');
    exportExcel(res.data.items.map(c => ({
      Nome: c.name, WhatsApp: fmtPhone(c.whatsapp), 'E-mail': c.email,
      Nascimento: fmtDate(c.birth_date),
      Avaliação: c.rating_type === 'positive' ? 'Positiva' : 'Negativa',
      Cadastro: fmtDate(c.created_at),
    })), `contatos-${data.config.slug}`, 'Contatos');
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-start justify-center p-3 overflow-y-auto" data-testid="company-profile-modal">
      <div className="bg-slate-50 rounded-2xl w-full max-w-3xl my-6 shadow-2xl">
        <header className="bg-[#0A2647] text-white px-5 py-4 rounded-t-2xl flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#C5A059] font-bold">Ficha da empresa</p>
            <h2 className="text-lg font-bold">{data?.basics?.name || 'Carregando...'}</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded" data-testid="close-profile">
            <X className="w-5 h-5" />
          </button>
        </header>

        {!data ? (
          <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
        ) : (
          <div className="p-5 space-y-4">
            {!!data.missing.length && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-sm text-red-700" data-testid="profile-missing">
                Faltando: {data.missing.join(' · ')}
              </div>
            )}

            <section className="bg-white rounded-xl border border-slate-200 p-4">
              <h3 className="font-bold text-[#0A2647] mb-3">Dados básicos</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Nome da empresa" value={form.name} onChange={v => setForm({ ...form, name: v })} />
                <Field label="CNPJ ou CPF" value={form.document} onChange={v => setForm({ ...form, document: v })} />
                <Field label="Endereço" value={form.address} onChange={v => setForm({ ...form, address: v })} />
                <Field label="Cidade" value={form.city} onChange={v => setForm({ ...form, city: v })} />
                <Field label="UF" value={form.state} onChange={v => setForm({ ...form, state: v })} />
                <Field label="Telefone" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
                <Field label="E-mail da empresa" value={form.contact_email} onChange={v => setForm({ ...form, contact_email: v })} />
                <Field label="Nome do dono" value={form.owner_name} onChange={v => setForm({ ...form, owner_name: v })} />
                <Field label="E-mail do dono" value={form.owner_email} onChange={v => setForm({ ...form, owner_email: v })} />
                <Field label="WhatsApp do dono" value={form.owner_whatsapp} onChange={v => setForm({ ...form, owner_whatsapp: v })} />
              </div>
            </section>

            <section className="bg-white rounded-xl border border-slate-200 p-4">
              <h3 className="font-bold text-[#0A2647] mb-3">Configurações</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Logomarca (URL)" value={form.logo_url} onChange={v => setForm({ ...form, logo_url: v })} />
                <Field label="Link do Google" value={form.google_maps_url} onChange={v => setForm({ ...form, google_maps_url: v })} />
                <Field label="Cor da placa" value={form.primary_color} onChange={v => setForm({ ...form, primary_color: v })} />
                <Field label="Link (slug)" value={form.slug} onChange={v => setForm({ ...form, slug: v })} />
              </div>
            </section>

            <div className="grid sm:grid-cols-2 gap-4">
              <section className="bg-white rounded-xl border border-slate-200 p-4">
                <h3 className="font-bold text-[#0A2647] mb-2">Plano e financeiro</h3>
                <dl className="text-sm space-y-1 text-slate-600">
                  <div className="flex justify-between"><dt>Plano</dt><dd className="font-semibold">{data.billing.plan_name}</dd></div>
                  <div className="flex justify-between"><dt>Status</dt><dd className="font-semibold">{STATUS_LABEL[data.billing.status] || data.billing.status}</dd></div>
                  <div className="flex justify-between"><dt>Ativação</dt><dd>{fmtDate(data.billing.activated_at)}</dd></div>
                  <div className="flex justify-between"><dt>Último boleto pago</dt>
                    <dd>{data.billing.last_payment ? `R$ ${data.billing.last_payment.value} · ${fmtDate(data.billing.last_payment.date)}` : '—'}</dd></div>
                  <div className="flex justify-between"><dt>Próximo vencimento</dt><dd>{fmtDate(data.billing.next_due_date)}</dd></div>
                </dl>
              </section>

              <section className="bg-white rounded-xl border border-slate-200 p-4" data-testid="profile-numbers">
                <h3 className="font-bold text-[#0A2647] mb-2">Números</h3>
                <dl className="text-sm space-y-1 text-slate-600">
                  <div className="flex justify-between"><dt>Total de avaliações</dt><dd className="font-semibold">{data.numbers.total_feedbacks}</dd></div>
                  <div className="flex justify-between"><dt>Positivas</dt><dd className="text-emerald-600 font-semibold">{data.numbers.positives}</dd></div>
                  <div className="flex justify-between"><dt>Negativas</dt><dd className="text-red-600 font-semibold">{data.numbers.negatives}</dd></div>
                  <div className="flex justify-between"><dt>Nota média</dt><dd className="font-semibold">{data.numbers.rating_average}</dd></div>
                  <div className="flex justify-between"><dt>Contatos coletados</dt><dd className="font-semibold">{data.numbers.total_contacts}</dd></div>
                </dl>
              </section>
            </div>

            <section className="bg-white rounded-xl border border-slate-200 p-4">
              <h3 className="font-bold text-[#0A2647] mb-2">Últimas ações de ativação</h3>
              {data.activation_logs.length ? (
                <ul className="space-y-2 max-h-48 overflow-y-auto" data-testid="profile-activation-logs">
                  {data.activation_logs.map(l => (
                    <li key={l.id} className="text-xs border-l-2 border-[#C5A059] pl-3">
                      <p className="text-slate-400">{new Date(l.created_at).toLocaleString('pt-BR')} · {l.action_type}</p>
                      <p className="text-slate-600">{l.message_text}</p>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-sm text-slate-400">Nenhuma ação enviada ainda.</p>}
            </section>

            <div className="flex flex-wrap gap-2 pb-2">
              <Button onClick={save} disabled={saving} className="bg-[#0A2647] hover:bg-[#0e3260] gap-2" data-testid="save-profile-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar
              </Button>
              <Button onClick={exportFicha} variant="outline" className="gap-2" data-testid="export-ficha-btn">
                <FileText className="w-4 h-4" /> Exportar Ficha
              </Button>
              <Button onClick={exportFeedbacks} variant="outline" className="gap-2" data-testid="export-feedbacks-btn">
                <FileSpreadsheet className="w-4 h-4" /> Exportar Avaliações
              </Button>
              <Button onClick={exportContacts} variant="outline" className="gap-2" data-testid="export-contacts-btn">
                <FileSpreadsheet className="w-4 h-4" /> Exportar Contatos
              </Button>
              <a href={`${API}/placa/${data.config.slug}`} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="gap-2" data-testid="view-plaques-btn">
                  <ImageIcon className="w-4 h-4" /> Ver Plaquinha
                </Button>
              </a>
            </div>

            <section className="bg-white rounded-xl border border-slate-200 p-4" data-testid="plaques-section">
              <h3 className="font-bold text-[#0A2647] mb-1">Plaquinhas para impressão</h3>
              <p className="text-xs text-slate-500 mb-3">
                Gera o PDF com o QR Code, a logo e as cores desta empresa — pronto para imprimir e mandar para o dono.
              </p>
              <div className="flex flex-wrap gap-2">
                {PLAQUE_FORMATS.map(fmt => (
                  <Button key={fmt.id} onClick={() => downloadPlaque(fmt.id)} disabled={plaqueLoading !== null}
                    size="sm" variant="outline" className="gap-2" data-testid={`plaque-${fmt.id}`}>
                    {plaqueLoading === fmt.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
                    {fmt.label}
                  </Button>
                ))}
                <Button onClick={() => { setWaPhone((data.basics.owner_whatsapp || '').split(',')[0].trim()); setWaOpen(true); }}
                  size="sm" className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="send-plaque-wa-btn">
                  <MessageCircle className="w-4 h-4" /> Enviar no WhatsApp do dono
                </Button>
              </div>
            </section>

            {waOpen && (
              <div className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4" data-testid="send-plaque-modal">
                <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3">
                  <div className="flex items-start justify-between">
                    <h3 className="font-bold text-[#0A2647]">Enviar plaquinha no WhatsApp</h3>
                    <button onClick={() => setWaOpen(false)} className="p-1 hover:bg-slate-100 rounded" data-testid="close-send-plaque">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600">Número de destino</label>
                    <input value={waPhone} onChange={e => setWaPhone(e.target.value)}
                      placeholder="(31) 99999-9999"
                      className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm"
                      data-testid="send-plaque-phone" />
                    <p className="text-[11px] text-slate-400 mt-1">
                      Já vem com o WhatsApp do dono. Dá para trocar antes de enviar.
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600">Recado antes da plaquinha (opcional)</label>
                    <textarea value={waNote} onChange={e => setWaNote(e.target.value)} rows={3}
                      className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm"
                      data-testid="send-plaque-note" />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => setWaOpen(false)}>Cancelar</Button>
                    <Button onClick={sendPlaqueWhatsapp} disabled={waSending || !waPhone.trim()}
                      size="sm" className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="confirm-send-plaque">
                      {waSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
                      Enviar agora
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const AdminRegistryTab = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const [f, setF] = useState({ q: '', plan: '', status: '', uf: '', completeness: '', order: 'az' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/registry/companies`, auth());
      setItems(res.data.items);
    } catch { toast.error('Erro ao carregar empresas'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter(c => {
    const q = f.q.trim().toLowerCase();
    if (q && !(`${c.name} ${c.document}`.toLowerCase().includes(q))) return false;
    if (f.plan && c.plan !== f.plan) return false;
    if (f.status && (c.status || 'none') !== f.status) return false;
    if (f.uf && (c.state || '').toUpperCase() !== f.uf) return false;
    if (f.completeness === 'complete' && c.incomplete) return false;
    if (f.completeness === 'incomplete' && !c.incomplete) return false;
    return true;
  }).sort((a, b) => {
    if (f.order === 'recent') return (b.created_at || '').localeCompare(a.created_at || '');
    if (f.order === 'most') return b.total_feedbacks - a.total_feedbacks;
    if (f.order === 'least') return a.total_feedbacks - b.total_feedbacks;
    return (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
  });

  const ufs = [...new Set(items.map(c => (c.state || '').toUpperCase()).filter(Boolean))].sort();
  const plans = [...new Set(items.map(c => c.plan))];

  const [fillingCities, setFillingCities] = useState(false);
  const fillCities = async () => {
    if (!window.confirm('Vou tentar descobrir cidade e estado pelo link do Google das empresas que estão sem. Continuar?')) return;
    setFillingCities(true);
    try {
      const r = await axios.post(`${API}/admin/registry/fill-cities`, {}, authHeaders());
      const { filled = [], failed = [], checked = 0 } = r.data;
      if (!checked) toast.info('Nenhuma empresa sem cidade com link do Google cadastrado.');
      else if (filled.length) toast.success(`${filled.length} cidade(s) preenchida(s): ${filled.slice(0, 3).join(' · ')}${filled.length > 3 ? '…' : ''}`, { duration: 9000 });
      if (failed.length) toast.warning(`${failed.length} link(s) não revelaram a cidade (link de "escrever avaliação" não traz endereço): ${failed.slice(0, 3).join(', ')}`, { duration: 11000 });
      load();
    } catch { toast.error('Erro ao preencher cidades'); }
    finally { setFillingCities(false); }
  };

  const exportRegistry = () => {
    if (!filtered.length) return toast.error('Nada para exportar');
    exportExcel(filtered.map(c => ({
      Empresa: c.name,
      'CNPJ/CPF': c.document || '',
      Dono: c.owner_name || '',
      'E-mail do dono': c.owner_email || '',
      WhatsApp: fmtPhone(c.whatsapp),
      Plano: c.plan_name,
      Status: STATUS_LABEL[c.status] || c.status,
      Cidade: c.city || '',
      UF: c.state || '',
      'Data de cadastro': fmtDate(c.created_at),
      'Total de avaliações': c.total_feedbacks,
      'Contatos coletados': c.total_contacts,
      'Cadastro incompleto': c.incomplete ? 'SIM' : 'NÃO',
      'O que falta': c.missing.join(' · '),
    })), `cadastro-empresas-${new Date().toISOString().slice(0, 10)}`, 'Empresas');
  };

  return (
    <div className="space-y-4" data-testid="admin-registry-tab">
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="relative col-span-2 lg:col-span-2">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={f.q} onChange={e => setF({ ...f, q: e.target.value })}
              placeholder="Buscar por nome ou CNPJ"
              className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="registry-search" />
          </div>
          <select value={f.plan} onChange={e => setF({ ...f, plan: e.target.value })}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="registry-filter-plan">
            <option value="">Todos os planos</option>
            {plans.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={f.status} onChange={e => setF({ ...f, status: e.target.value })}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="registry-filter-status">
            <option value="">Todos os status</option>
            <option value="active">Ativo</option>
            <option value="trial">Trial</option>
            <option value="overdue">Inadimplente</option>
            <option value="canceled">Cancelado</option>
            <option value="none">Sem cobrança</option>
          </select>
          <select value={f.uf} onChange={e => setF({ ...f, uf: e.target.value })}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="registry-filter-uf">
            <option value="">Todos os estados</option>
            {ufs.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
          <select value={f.completeness} onChange={e => setF({ ...f, completeness: e.target.value })}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="registry-filter-complete">
            <option value="">Completos e incompletos</option>
            <option value="complete">Só cadastro completo</option>
            <option value="incomplete">Só incompletos</option>
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={f.order} onChange={e => setF({ ...f, order: e.target.value })}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="registry-order">
            <option value="az">Ordem alfabética</option>
            <option value="recent">Cadastro mais recente</option>
            <option value="most">Mais avaliações</option>
            <option value="least">Menos avaliações</option>
          </select>
          <span className="text-sm text-slate-500" data-testid="registry-count">
            {filtered.length} empresa(s) · {filtered.filter(c => c.incomplete).length} com cadastro incompleto
          </span>
          <Button onClick={fillCities} size="sm" variant="outline" disabled={fillingCities}
            className="gap-2 ml-auto" data-testid="fill-cities-btn">
            {fillingCities ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
            Preencher cidades pelo Google
          </Button>
          <Button onClick={exportRegistry} size="sm" className="bg-emerald-600 hover:bg-emerald-700 gap-2"
            data-testid="export-registry-btn">
            <FileSpreadsheet className="w-4 h-4" /> Exportar Cadastro
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="registry-table">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-3">Empresa</th>
                  <th className="text-left px-3 py-3">CNPJ/CPF</th>
                  <th className="text-left px-3 py-3">Dono</th>
                  <th className="text-left px-3 py-3">WhatsApp</th>
                  <th className="text-left px-3 py-3">Plano</th>
                  <th className="text-center px-3 py-3">Status</th>
                  <th className="text-left px-3 py-3">Cidade/UF</th>
                  <th className="text-center px-3 py-3">Cadastro</th>
                  <th className="text-center px-3 py-3">Avaliações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(c => (
                  <tr key={c.id} onClick={() => setOpen(c.id)}
                    className="hover:bg-slate-50 cursor-pointer" data-testid={`registry-row-${c.id}`}>
                    <td className="px-3 py-2.5">
                      <div className="font-medium text-slate-800 flex items-center gap-2">
                        {c.name}
                        {c.incomplete && (
                          <span className="text-[9px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-bold uppercase"
                            title={c.missing.join(' · ')} data-testid={`incomplete-tag-${c.id}`}>Incompleto</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400">{c.owner_email}</p>
                    </td>
                    <td className="px-3 py-2.5 text-slate-600">{c.document || '—'}</td>
                    <td className="px-3 py-2.5 text-slate-600">{c.owner_name || '—'}</td>
                    <td className="px-3 py-2.5 text-slate-600">{fmtPhone(c.whatsapp)}</td>
                    <td className="px-3 py-2.5 text-slate-600">{c.plan_name}</td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${STATUS_STYLE[c.status] || 'bg-slate-100 text-slate-500'}`}>
                        {STATUS_LABEL[c.status] || c.status}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-slate-600">{c.city ? `${c.city}/${c.state}` : '—'}</td>
                    <td className="px-3 py-2.5 text-center text-slate-500">{fmtDate(c.created_at)}</td>
                    <td className="px-3 py-2.5 text-center font-semibold text-[#0A2647]">{c.total_feedbacks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!filtered.length && (
              <p className="py-12 text-center text-sm text-slate-500">Nenhuma empresa com esses filtros.</p>
            )}
          </div>
        )}
      </div>

      {open && <CompanyProfile companyId={open} onClose={() => setOpen(null)} onSaved={load} />}
    </div>
  );
};

export default AdminRegistryTab;
